import { useMemo } from 'react'
import { BaseEdge, getBezierPath, type Position } from '@xyflow/react'
import { EDGE_CONFIG } from '../../../constants/mindmapConfig'
import { useZoomState } from '../hooks/useZoomState'

interface LabeledEdgeProps {
  sourceX: number
  sourceY: number
  targetX: number
  targetY: number
  sourcePosition: Position
  targetPosition: Position
  style?: React.CSSProperties
  data?: {
    targetNode: {
      id: string
    }
    /** Override color for stroke and arrow (used by branch selection mode) */
    colorOverride?: string
  }
}

/**
 * Custom edge component that displays tag indicators for descendant nodes
 * Shows colored circles representing tags on nodes at the end of the tree
 */
export function LabeledEdge({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  data,
}: LabeledEdgeProps) {
  // Get zoom state - uses optimized hook that only re-renders when zoom crosses thresholds
  // This dramatically reduces re-renders during pan/zoom (edges only update at 0.05 zoom steps)
  const { zoomScale, zoomScaleRaw } = useZoomState()

  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })

  // Compute final edge style
  // Scale stroke width by zoomScaleRaw for zoom-independent sizing
  const edgeStyle = useMemo(() => {
    const baseStrokeWidth = (style.strokeWidth as number) || EDGE_CONFIG.DEFAULT_STROKE_WIDTH
    return {
      ...style,
      stroke: data?.colorOverride ?? EDGE_CONFIG.DEFAULT_COLOR,
      strokeWidth: baseStrokeWidth * zoomScaleRaw,
      cursor: 'default',
      pointerEvents: 'none' as const,
    }
  }, [style, zoomScaleRaw, data?.colorOverride])

  // Generate a unique marker ID for this edge's zoom level
  const effectiveMarkerColor = data?.colorOverride ?? EDGE_CONFIG.DEFAULT_COLOR

  // Scale marker dimensions by zoomScaleRaw for zoom-independent sizing
  const markerSize = EDGE_CONFIG.MARKER_WIDTH * zoomScaleRaw
  const refXScaled = 10 * zoomScaleRaw // Keep proportional offset for arrow tip
  const refYScaled = 6 * zoomScaleRaw // Center point of arrow

  const markerId = useMemo(() => {
    // Create a safe ID from the color and zoomScale (quantized to reduce unique markers)
    // Using zoomScale (quantized) instead of raw zoom reduces marker count significantly
    const colorId = effectiveMarkerColor.replace(/[^a-zA-Z0-9]/g, '')
    const zoomId = Math.round(zoomScale * 100) // Quantized zoom, fewer unique IDs
    return `edge-arrow-${colorId}-${zoomId}`
  }, [effectiveMarkerColor, zoomScale])

  return (
    <>
      {/* Define custom marker - uses tag color if present, otherwise default grey */}
      {/* Marker dimensions scaled by zoomScale for zoom-independent sizing */}
      <defs>
        <marker
          id={markerId}
          markerWidth={markerSize}
          markerHeight={markerSize}
          refX={refXScaled}
          refY={refYScaled}
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path
            d={`M0,0 L0,${markerSize} L${markerSize},${markerSize / 2} z`}
            fill={effectiveMarkerColor}
          />
        </marker>
      </defs>
      <BaseEdge path={edgePath} markerEnd={`url(#${markerId})`} style={edgeStyle} />
    </>
  )
}
